import classNames from "classnames";
import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/utils/cssHelpers";

const buttonVariants = cva(
  "font-sans-serif uppercase font-medium rounded-sm cursor-pointer text-center flex items-center justify-center gap-2.5 w-fit",
  {
    variants: {
      intent: {
        primary:
          "bg-gradient-bright text-white hover:opacity-90 active:opacity-80 hover:text-white",
        secondary:
          "bg-deep-blue text-white hover:text-white hover:bg-mid-neutral-1 active:bg-mid-neutral-2",
        tertiary:
          "bg-gradient-light text-black hover:opacity-90 active:opacity-80 hover:text-black",
        success: "bg-success text-white hover:text-white hover:bg-light-success",
        warning: "bg-warning text-white hover:text-white hover:bg-light-warning",
        danger: "bg-danger text-white hover:text-white hover:bg-light-danger",
        info: "bg-info text-white hover:text-white hover:bg-light-info",
        purple: "bg-purple text-white hover:text-white hover:bg-light-purple",
      },
      size: {
        xsmall: "text-xs py-1 px-3",
        small: "text-sm py-1.5 px-3",
        medium: "text-base py-2 px-4",
        large: "text-lg py-2.5 px-5",
        xlarge: "text-xl py-3 px-7",
      },
      outlined: {
        true: "border border-deep-blue bg-transparent text-deep-blue hover:text-deep-blue hover:bg-light-neutral-3",
        false: "",
      },
      minimal: {
        true: "bg-transparent text-deep-blue hover:text-deep-blue hover:bg-light-neutral-3",
        false: "",
      },
      rounded: {
        true: "rounded-full",
        false: "",
      },
      loading: {
        true: "opacity-50 cursor-not-allowed hover:opacity-50 active:opacity-50",
        false: "",
      },
      disabled: {
        true: "opacity-50 cursor-not-allowed hover:opacity-50 active:opacity-50",
        false: "",
      },
      fill: {
        true: "w-full",
        false: "",
      },
    },
    compoundVariants: [
      {
        outlined: true,
        loading: true,
        class: "border-light-neutral-2",
      },
      {
        outlined: true,
        disabled: true,
        class: "border-light-neutral-2",
      },
      {
        outlined: true,
        intent: "primary",
        class: "border-orange text-orange hover:text-white hover:bg-orange",
      },
      {
        outlined: true,
        intent: "secondary",
        class: "border-deep-blue text-deep-blue hover:text-white hover:bg-deep-blue",
      },
      {
        outlined: true,
        intent: "tertiary",
        class: "border-dark-neutral-3 text-dark-neutral-3 hover:text-white hover:bg-dark-neutral-3",
      },
      {
        outlined: true,
        intent: "success",
        class: "border-success text-success hover:text-white hover:bg-success",
      },
      {
        outlined: true,
        intent: "warning",
        class: "border-warning text-warning hover:text-white hover:bg-warning",
      },
      {
        outlined: true,
        intent: "danger",
        class: "border-danger text-danger hover:text-white hover:bg-danger",
      },
      {
        outlined: true,
        intent: "info",
        class: "border-info text-info hover:text-white hover:bg-info",
      },
      {
        outlined: true,
        intent: "purple",
        class: "border-purple text-purple hover:text-white hover:bg-purple",
      },
    ],
    defaultVariants: {
      intent: "primary",
      size: "medium",
      outlined: false,
      minimal: false,
      rounded: false,
    },
  },
);

export type CaButtonSize = "xsmall" | "small" | "medium" | "large" | "xlarge";
export type CaButtonIntent =
  | "primary"
  | "secondary"
  | "tertiary"
  | "danger"
  | "success"
  | "warning"
  | "info"
  | "purple";

export type CaButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    children?: React.ReactNode;
    className?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    disabled?: boolean;
    loading?: boolean;
    outlined?: boolean;
    minimal?: boolean;
    size?: CaButtonSize;
    intent?: CaButtonIntent;
    rounded?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    type?: "button" | "submit" | "reset";
    fill?: boolean;
  };

function CaButton({
  children,
  className,
  onClick,
  disabled,
  loading,
  outlined,
  minimal,
  size,
  href,
  intent,
  rounded,
  leftIcon,
  rightIcon,
  type = "button",
  fill,
  ...props
}: CaButtonProps) {
  const Component = href ? "a" : "button";

  return (
    <Component
      type={type}
      className={cn(
        buttonVariants({
          intent: intent ?? "primary",
          size,
          outlined,
          minimal,
          rounded,
          loading,
          disabled,
          fill,
        }),
        className,
      )}
      onClick={onClick}
      disabled={disabled || loading}
      href={href}
      {...props}
    >
      {leftIcon && (
        <span className={classNames("flex items-center justify-center")}>{leftIcon}</span>
      )}
      {loading ? "Loading..." : children}
      {rightIcon && (
        <span className={classNames("flex items-center justify-center")}>{rightIcon}</span>
      )}
    </Component>
  );
}

CaButton.defaultProps = {
  intent: "primary",
  size: "medium",
  disabled: false,
  loading: false,
  outlined: false,
  minimal: false,
  rounded: false,
};

export default CaButton;
